import { createContext, useContext } from 'react';
import {
    ApprovalStatus,
    CommentActivity,
    LabelDetailed,
    StatusChangeActivity,
    UploadActivity,
    UserFeedbackActivity,
} from '../types';

// Your existing state interface
export interface LabelState {
    labelDetailed: LabelDetailed;
    imageUrl: string;
    activities: (
        | StatusChangeActivity
        | CommentActivity
        | UploadActivity
        | UserFeedbackActivity
    )[];
    focussedRule: string | null;
    loadingState: 'loading' | 'loaded' | 'error';
    errorMessage: string | null;
}

export interface LabelHandlers {
    handleApprovalStatusChange: (
        assetId: string,
        newStatus: ApprovalStatus,
        reason?: string,
    ) => Promise<void>;
    handleCommentSubmission: (
        assetId: string,
        comment: string,
    ) => Promise<void>;
    handleStageReview: (
        stageId: string,
        status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED,
        reason?: string,
    ) => Promise<void>;
    handleLabelNameUpdate: (labelId: string, name: string) => Promise<void>;
    handleLabelNotesUpdate: (labelId: string, notes: string) => Promise<void>;
}

// New interface for setters
export interface LabelSetters {
    setFocussedRule: React.Dispatch<React.SetStateAction<string | null>>;
}

// Update the context to include both state and setters
export interface LabelContextType {
    state: LabelState;
    setters: LabelSetters;
    handlers: LabelHandlers;
}

export const LabelContext = createContext<LabelContextType | null>(null);

export function useLabelContext() {
    const labelContext = useContext(LabelContext);

    if (labelContext === null) {
        throw new Error('useLabelContext must be used with a LabelContext');
    }

    return labelContext;
}
